// @em/blocks — deterministic date/time formatting for block views.
//
// Views render during SSR (on the server) AND hydrate on the client, so any date they
// format must produce the SAME string in both places — otherwise React reports a
// hydration mismatch. The runtime default locale + timezone differ (server: Node en-US
// "3:00 PM"; browser: the viewer's locale "3:00 p.m.", and a different timezone), so we
// always format with a FIXED locale ('en-US') + the EVENT's timezone. That is also the
// correct thing to show: an event's times in the event's own local time, for everyone.
//
// ⚠ An event with NO time zone is shown in UTC — never in "the runtime's" zone (2026-10-03, suggested task "Fix
//   runtime-locale dates"). `timeZone: undefined` means the SERVER's zone on the server and the VIEWER's in the browser:
//   the same instant printed twice, and React throws the server's HTML away. A fixed zone is at least the same zone.
// ⚠ A block's "close on" value is a FLOATING wall clock ("2026-10-10 17:00:00", no zone) — format it with
//   `formatWallClock`, never `formatInZone`: `new Date("2026-10-10 17:00:00")` is parsed in the RUNTIME's zone, so the
//   Worker (UTC) and a Toronto browser printed two different times for one setting.

/** The locale every date on the public site is written in (server and browser alike). */
export const PUBLIC_DATE_LOCALE = 'en-US';

/** The zone to format in: the one given, else UTC (never the runtime's). */
export const zoneOrUtc = (tz: string | null | undefined): string => String(tz ?? '').trim() || 'UTC';

export const formatInZone = (
  iso: string | null | undefined,
  tz: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  try {
    return new Intl.DateTimeFormat(PUBLIC_DATE_LOCALE, { ...options, timeZone: zoneOrUtc(tz) }).format(d);
  } catch {
    // An IANA name this runtime does not know: UTC, never the runtime's own zone.
    return new Intl.DateTimeFormat(PUBLIC_DATE_LOCALE, { ...options, timeZone: 'UTC' }).format(d);
  }
};

/**
 * A FLOATING wall clock ("YYYY-MM-DD HH:mm[:ss]", the no-zone value the admin DateTimeField stores) written as it reads —
 * "Oct 10, 5:00 PM" for "2026-10-10 17:00:00" — whatever the runtime's zone. It is already the host's local time, so no
 * zone is applied: the parts are read as written and formatted in UTC. '' for an empty or unreadable value.
 */
export const formatWallClock = (wall: string | null | undefined, options: Intl.DateTimeFormatOptions): string => {
  const m = String(wall ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) {
    return '';
  }
  const [, y, mo, d, hh = '0', mi = '0', ss = '0'] = m;
  const asUtc = new Date(Date.UTC(+y, +mo - 1, +d, +hh, +mi, +ss));
  if (Number.isNaN(asUtc.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(PUBLIC_DATE_LOCALE, { ...options, timeZone: 'UTC' }).format(asUtc);
};

/**
 * The calendar day ("YYYY-MM-DD") an instant falls on IN A ZONE — what groups a schedule by day or picks the weather for
 * the event's day. ⚠ Never `iso.slice(0, 10)`: that is the UTC day, so an 8 PM session in Toronto (00:00Z) was filed
 * under the next day.
 */
export const dayKeyInZone = (iso: string | null | undefined, tz: string | null | undefined): string => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const partsIn = (zone: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = partsIn(zoneOrUtc(tz));
  } catch {
    parts = partsIn('UTC');
  }
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';

  return `${y}-${m}-${d}`;
};

/**
 * Interpret a FLOATING wall-clock ("YYYY-MM-DD HH:mm:ss", the no-zone value the admin
 * DateTimeField stores) AS a time in `tz`, returning the absolute epoch ms. So a host who
 * sets a block's "close on" to 5pm on the 10th means 5pm in the EVENT's (or the item's)
 * own timezone, and it closes at that instant everywhere. Deterministic — no Date.now / no
 * locale dependence — so it is SSR-safe (the caller supplies `now`, see isDeadlinePassed).
 * With no tz the wall-clock is treated as UTC.
 */
export const zonedWallClockToInstant = (wall: string | null | undefined, tz?: string | null): number | null => {
  if (!wall) {
    return null;
  }
  const m = String(wall).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) {
    return null;
  }
  const [, y, mo, d, hh = '0', mi = '0', ss = '0'] = m;
  const utcGuess = Date.UTC(+y, +mo - 1, +d, +hh, +mi, +ss);
  if (!tz) {
    return utcGuess;
  }

  // Offset (local = utc + offset) that `tz` applies at a given instant, via Intl parts.
  const offsetAt = (instant: number): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(instant));
    const g: Record<string, number> = {};
    for (const p of parts) {
      if (p.type !== 'literal') {
        g[p.type] = Number(p.value);
      }
    }
    const asIfUtc = Date.UTC(g.year, g.month - 1, g.day, g.hour, g.minute, g.second);
    return asIfUtc - instant;
  };

  try {
    const off1 = offsetAt(utcGuess);
    // Second pass so a DST transition near the target resolves to the right offset.
    const off2 = offsetAt(utcGuess - off1);
    return utcGuess - off2;
  } catch {
    return utcGuess;
  }
};

/**
 * Whether a block "close on" deadline (a floating wall-clock in `tz`) is in the past as of
 * `now` (epoch ms). The caller passes `now` explicitly so a view can keep it null during
 * SSR + first paint (→ never closed) and set it in a mount effect (→ closes on the client),
 * avoiding a hydration mismatch.
 */
export const isDeadlinePassed = (
  wall: string | null | undefined,
  tz: string | null | undefined,
  now: number | null,
): boolean => {
  if (now == null) {
    return false;
  }
  const instant = zonedWallClockToInstant(wall, tz);
  return instant != null && now >= instant;
};
